import { ChangeDetectionStrategy, Component, EventEmitter, Input, OnInit, Output, ViewChild, ViewEncapsulation } from '@angular/core';
import { redo, redoDepth, undo, undoDepth } from '@codemirror/commands';
import { Command, EditorView } from '@codemirror/view';
import { CodeLang } from '@common/codemirror/types/code-lang.type';
import { getSchemaByFileType } from '@common/util/schema-set';
import { CodeEditorService } from '@portal-core/code-editor/services/code-editor.service';
import { ProjectFileType } from '@portal-core/project-files/enums/project-file-type.enum';
import { EditorToolbarComponent } from '@portal-core/ui/editor/components/editor-toolbar/editor-toolbar.component';
import { EditorToolbarDropdownMenuClosedEvent } from '@portal-core/ui/editor/types/editor-toolbar-dropdown-menu-closed-event.type';
import { EditorToolbarItem } from '@portal-core/ui/editor/types/editor-toolbar-item.type';
import { EditorToolbarControl } from '@portal-core/ui/editor/util/editor-toolbar-control';
import { InputObservable } from '@portal-core/util/input-observable.decorator';
import { Observable, map } from 'rxjs';

@Component({
  selector: 'mc-code-editor-toolbar',
  templateUrl: './code-editor-toolbar.component.html',
  styleUrls: ['./code-editor-toolbar.component.scss'],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class CodeEditorToolbarComponent implements OnInit {
  @Input() lang: CodeLang;
  @Input() showLang?: boolean = false;
  @Input() wrapLines?: boolean;
  @Input() projectFileType: ProjectFileType;

  @InputObservable('lang') lang$: Observable<string>;

  @Output() dispatch: EventEmitter<Command | Command[]> = new EventEmitter<Command | Command[]>();
  @Output() dropdownMenuClosed: EventEmitter<EditorToolbarDropdownMenuClosedEvent<Command>> = new EventEmitter<EditorToolbarDropdownMenuClosedEvent<Command>>();
  @Output() toggleWrapLines: EventEmitter<void> = new EventEmitter<void>();
  @ViewChild(EditorToolbarComponent, { static: true }) toolbar: EditorToolbarComponent<Command, EditorView>;

  toolbarControl$: Observable<EditorToolbarControl<Command>>;

  constructor(private codeEditorService: CodeEditorService) { }

  ngOnInit() {
    this.toolbarControl$ = this.lang$.pipe(
      map(() => this.createToolbarItems())
    );
  }

  updateState(editorView: EditorView) {
    this.toolbar.updateState(editorView);
  }

  onItemClicked(item: EditorToolbarItem<Command>) {
    if (item.command) {
      this.dispatch.emit(item.command);
    }
  }

  onDropdownMenuClosed(event: EditorToolbarDropdownMenuClosedEvent<Command>) {
    this.dropdownMenuClosed.emit(event);
  }

  createToolbarItems(): EditorToolbarControl<Command> {
    return new EditorToolbarControl<Command>([
      {
        type: 'button',
        icon: 'icon-undo',
        tooltip: 'Undo',
        command: undo,
        disabled: true,
        isDisabled: (editorView: EditorView) => undoDepth(editorView.state) === 0
      }, {
        type: 'button',
        icon: 'icon-redo',
        tooltip: 'Redo',
        command: redo,
        disabled: true,
        isDisabled: (editorView: EditorView) => redoDepth(editorView.state) === 0
      }, {
        type: 'divider',
      }, {
        type: 'button',
        icon: 'icon-line-wrap',
        tooltip: 'Toggle Line Wrapping',
        command: () => {
          this.toggleWrapLines.emit();
          return true;
        },
        isActive: () => this.wrapLines,
      }, {
        type: 'button',
        icon: 'icon-format-code',
        tooltip: typeof this.lang !== 'string' ? 'Unknown language: cannot format code' : 'Format Code',
        disabled: false,
        isDisabled: () => typeof this.lang !== 'string',
        command: (editorView: EditorView) => {
          var content = editorView.state.doc.toString();
          const schema = this.projectFileType ? getSchemaByFileType(this.projectFileType) : null;
          if (schema) {
            // an exception can occur for invalid files
            try {
              const doc = schema.codeToDoc(content);
              content = schema.nodeToCode(doc);
            }
            catch { }
          }
          editorView.dispatch({
            changes: {
              from: 0,
              to: editorView.state.doc.length,
              insert: this.projectFileType && schema
                ? this.codeEditorService.formatByFileType(content, this.projectFileType)
                : this.codeEditorService.formatByLang(content, this.lang)
            }
          });

          return true;
        }
      }, {
        type: 'fill',
        hidden: !this.showLang
      }, {
        type: 'custom',
        hidden: !this.showLang,
        templateName: 'lang'
      }
    ]);
  }
}
